import { keepPreviousQueryData, useInfiniteQuery } from '@northladder/services';
import { IApiError } from '@northladder/utilities';

import { BIDS_API } from '../bidsAPIClient';
import { IFetchAllStagedBidsReportPayload } from '../types';

/**
 * This is to track the staged bids report listing key in react query cache.
 */
export const FETCH_ALL_STAGED_BIDS_REPORT_KEY =
  'fetch-all-staged-bids-report-key';

/**
 * This performs a get request and retrieves the staged bids report data by
 * category and brands.
 * Provides data fetching states to be used in the staged bids report list.
 */
export const useSearchStagedBidsReportQuery = (
  payload: IFetchAllStagedBidsReportPayload
) => {
  const {
    bidValidityConfigId = '',
    biddingWindowId = '',
    categoryId = '',
    count,
    searchText = '',
  } = payload;

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    refetch,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      FETCH_ALL_STAGED_BIDS_REPORT_KEY,
      bidValidityConfigId,
      biddingWindowId,
      categoryId,
      count,
      searchText,
    ],
    networkMode: 'always', // Even make calls when offline
    queryFn: ({ pageParam = 1 }) =>
      BIDS_API.searchAllStagedBidsReport({ ...payload, page: pageParam }),
    initialPageParam: 1,
    placeholderData: keepPreviousQueryData,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const currentCount = allPages.length * count;

      return currentCount < lastPage.totalCount ? nextPage : undefined;
    },
  });

  return {
    data,
    error: error as unknown as IApiError<Error | null>,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    refetch,
  };
};
